import {useQuery, type QueryKey} from '@github-ui/react-query'
import {reactFetch} from '@github-ui/verified-fetch'
import {PageData} from '@github-ui/pull-request-page-data-tooling/page-data'
import type {FileTreePayload} from '../payloads/file-tree'
import {buildDiffPathOrderMap} from '../../helpers/diff-helpers'
import {useLayoutStoreActions} from '../../stores/layout-selectors'

function fileTreeQueryKey(basePath: string): QueryKey {
  return [PageData.fileTree, basePath]
}

function fileTreeApiUrl(basePath: string): string {
  return `${basePath}/page_data/${PageData.fileTree}`
}

/**
 * Lazily fetches the file_tree endpoint and hydrates the layout store with
 * the diff path order map. Intended for use when the comments side panel opens
 * on a page that doesn't have files-changed route data (e.g. conversation tab).
 */
export function useFileTreeDiffSummaries({basePath, enabled}: {basePath: string; enabled: boolean}) {
  const {hydrateDiffPathOrderMap} = useLayoutStoreActions()

  const query = useQuery<FileTreePayload>({
    queryKey: fileTreeQueryKey(basePath),
    queryFn: async () => {
      const result = await reactFetch(fileTreeApiUrl(basePath))
      if (!result.ok)
        throw new Error(`Failed to fetch file tree from ${fileTreeApiUrl(basePath)}: HTTP ${result.status}`)
      const data: FileTreePayload = await result.json()
      const diffs = buildDiffPathOrderMap(data.diffs)
      hydrateDiffPathOrderMap(diffs)
      return data
    },
    enabled,
    staleTime: Infinity,
  })

  return query
}
